import React, { useState, useEffect, useMemo } from 'react';
import { tkbAPI, lopHocAPI, giaoVienAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import XLSXStyle from 'xlsx-js-style';
import { FiDownload } from 'react-icons/fi';

const THU_LIST = [
  { value: 2, label: 'Thứ 2', short: 'T2' },
  { value: 3, label: 'Thứ 3', short: 'T3' },
  { value: 4, label: 'Thứ 4', short: 'T4' },
  { value: 5, label: 'Thứ 5', short: 'T5' },
  { value: 6, label: 'Thứ 6', short: 'T6' },
  { value: 7, label: 'Thứ 7', short: 'T7' }
];

const BUOI_LIST = [
  { value: 'Sang', label: 'Sáng', short: 'S' },
  { value: 'Chieu', label: 'Chiều', short: 'C' }
];

const TIET_LIST = [1, 2, 3, 4, 5];

const KetQuaTKBPage = () => {
  const { t } = useTranslation();
  const [tkbs, setTkbs] = useState([]);
  const [lopHocs, setLopHocs] = useState([]);
  const [giaoViens, setGiaoViens] = useState([]);
  const [loading, setLoading] = useState(true);

  const [resultView, setResultView] = useState('lop_sang'); // 'lop_sang' | 'lop_chieu' | 'lop_view' | 'gv'
  const [highlightGvId, setHighlightGvId] = useState(null);

  const thuList = THU_LIST;
  const buoiList = BUOI_LIST;
  const tietList = TIET_LIST;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tkbRes, lopRes, gvRes] = await Promise.all([
        tkbAPI.getAll(),
        lopHocAPI.getAll(),
        giaoVienAPI.getAll()
      ]);
      setTkbs(tkbRes.data?.success ? (tkbRes.data.data || []) : []);
      setLopHocs(lopRes.data?.success ? (lopRes.data.data || []) : []);
      setGiaoViens(gvRes.data?.success ? (gvRes.data.data || []) : []);
    } catch (error) {
      toast.error(t('ketQua.cannotLoadTKB'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findTKB = (lopId, thu, buoi, tiet) => {
    return tkbs.find(t => {
      if (t.lop_id !== lopId || t.thu !== thu || t.buoi !== buoi) return false;
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      return tiet >= t.tiet && tiet <= (t.tiet + soTiet - 1);
    });
  };

  const getMonShortName = (tenMon) => {
    if (!tenMon) return '';
    if (tenMon.length <= 4) return tenMon;
    return tenMon.split(' ').map(w => w.charAt(0)).join('').toUpperCase();
  };

  const getGvShortName = (tenGv) => {
    if (!tenGv) return '';
    const parts = tenGv.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
    // Giữ nguyên tên (từ cuối), viết tắt họ và đệm
    const lastName = parts[parts.length - 1];
    const initials = parts.slice(0, -1).map(w => w.charAt(0).toUpperCase()).join('');
    return initials + '.' + lastName;
  };

  const getGvColor = (gvId) => {
    if (!gvId) return 'transparent';
    const colors = [
      '#ffeaa7', '#fdcb6e', '#e17055', '#d63031', '#e84393',
      '#fd79a8', '#a29bfe', '#6c5ce7', '#74b9ff', '#0984e3',
      '#00b894', '#00cec9', '#55a3ff', '#fd79a8', '#e84393',
      '#d63031', '#e17055', '#fdcb6e', '#ffeaa7', '#a29bfe'
    ];
    let hash = 0;
    const str = gvId.toString();
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getLopColor = (lopId) => {
    if (!lopId) return 'transparent';
    const colors = [
      '#b2dfdb', '#80cbc4', '#c8e6c9', '#a5d6a7',
      '#bbdefb', '#90caf9', '#e1bee7', '#ce93d8',
      '#ffe0b2', '#ffcc80', '#f8bbd9', '#f48fb1',
      '#dcedc8', '#c5e1a5', '#fff9c4', '#b3e5fc',
      '#d7ccc8', '#bcaaa4', '#cfd8dc', '#b0bec5'
    ];
    let hash = 0;
    const str = lopId.toString();
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getContrastTextColor = (bgColor) => {
    if (!bgColor || bgColor === 'transparent') return '#111';
    const hex = bgColor.replace('#', '').trim();
    if (hex.length !== 6) return '#111';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return '#111';
    const srgb = [r, g, b].map(v => v / 255);
    const lin = srgb.map(c => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
    return L < 0.5 ? '#fff' : '#111';
  };

  const handleExportExcel = () => {
    if (!tkbs.length) {
      toast.info(t('ketQua.noTKB'));
      return;
    }

    // Helper: add/merge border sides onto a cell
    const addBorder = (ws, r, c, sides) => {
      const addr = XLSXStyle.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = ws[addr].s || {};
      ws[addr].s.border = ws[addr].s.border || {};
      Object.assign(ws[addr].s.border, sides);
    };
    const THICK = { style: 'medium', color: { rgb: '595959' } };
    const applySpecs = (ws, specs) => {
      specs.forEach(({ r, c, cStart, cEnd, side }) => {
        if (cStart !== undefined) {
          for (let col = cStart; col <= cEnd; col++) addBorder(ws, r, col, { [side]: THICK });
        } else {
          addBorder(ws, r, c, { [side]: THICK });
        }
      });
    };

    // Auto-fit columns by scanning max cell content length per column
    const autoFitColumns = (ws) => {
      const widths = {};
      Object.keys(ws).forEach(addr => {
        if (addr[0] === '!') return;
        const ref = XLSXStyle.utils.decode_cell(addr);
        if (!ref) return;
        const cell = ws[addr];
        const len = cell.v !== undefined && cell.v !== null ? String(cell.v).length : 0;
        if (!widths[ref.c] || len > widths[ref.c]) widths[ref.c] = len;
      });
      const maxCol = Object.keys(widths).length ? Math.max(...Object.keys(widths).map(Number)) : 0;
      const cols = [];
      for (let i = 0; i <= maxCol; i++) cols.push({ wch: Math.min(Math.max((widths[i] || 4) + 2, 6), 50) });
      ws['!cols'] = cols;
    };

    const workbook = XLSXStyle.utils.book_new();

    // == Sheet "TKB Lớp": Sáng block + Chiều block stacked in one sheet ==
    const buildLopSheet = () => {
      const aoa = [];
      const merges = [];
      const borderSpecs = [];
      let hasData = false;

      const addBlock = (buoiValue, title) => {
        const activeLops = lopHocs.filter(lop =>
          tkbs.some(t => t.lop_id === lop.lop_id && t.buoi === buoiValue)
        );
        if (activeLops.length === 0) return;
        hasData = true;
        const nLop = activeLops.length;
        const nCols = 2 + nLop * 2;

        // Title row
        aoa.push([title]);

        // Header row 1: Thứ | Tiết | [Lop1 merged×2] | [Lop2 merged×2] | ...
        const hr1 = ['Thứ', 'Tiết'];
        activeLops.forEach(lop => { hr1.push(lop.ten_lop); hr1.push(''); });
        const hr1Row = aoa.length;
        aoa.push(hr1);
        activeLops.forEach((_, i) => {
          merges.push({ s: { r: hr1Row, c: 2 + i * 2 }, e: { r: hr1Row, c: 3 + i * 2 } });
        });

        // Header row 2: "" | "" | Môn học | Giáo viên | ...
        const hr2 = ['', ''];
        activeLops.forEach(() => { hr2.push('Môn học'); hr2.push('Giáo viên'); });
        aoa.push(hr2);

        // Data rows: 6 thứ × 5 tiết
        const dataStart = aoa.length;
        thuList.forEach(thu => {
          const thuStart = aoa.length;
          tietList.forEach((tiet, idx) => {
            const row = [idx === 0 ? thu.label : '', tiet];
            activeLops.forEach(lop => {
              const tkb = findTKB(lop.lop_id, thu.value, buoiValue, tiet);
              row.push(tkb ? (tkb.ten_mon || '') : '');
              row.push(tkb ? getGvShortName(tkb.ten_gv) : '');
            });
            aoa.push(row);
          });
          merges.push({ s: { r: thuStart, c: 0 }, e: { r: thuStart + tietList.length - 1, c: 0 } });
          // Bottom border after last tiết of this Thứ
          borderSpecs.push({ r: thuStart + tietList.length - 1, cStart: 0, cEnd: nCols - 1, side: 'bottom' });
        });
        const dataEnd = aoa.length - 1;

        // Right border: after Tiết col and after each GV col
        for (let r = dataStart; r <= dataEnd; r++) {
          borderSpecs.push({ r, c: 1, side: 'right' });
          for (let i = 0; i < nLop; i++) {
            borderSpecs.push({ r, c: 3 + i * 2, side: 'right' });
          }
        }
      };

      addBlock('Sang', 'THỜI KHÓA BIỂU BUỔI SÁNG');
      aoa.push([]); // blank separator row
      addBlock('Chieu', 'THỜI KHÓA BIỂU BUỔI CHIỀU');
      if (!hasData) return null;

      const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
      ws['!merges'] = merges;
      applySpecs(ws, borderSpecs);

      const maxLops = Math.max(
        lopHocs.filter(l => tkbs.some(t => t.lop_id === l.lop_id && t.buoi === 'Sang')).length,
        lopHocs.filter(l => tkbs.some(t => t.lop_id === l.lop_id && t.buoi === 'Chieu')).length
      );
      const cols = [{ wch: 8 }, { wch: 6 }];
      for (let i = 0; i < maxLops; i++) { cols.push({ wch: 18 }); cols.push({ wch: 12 }); }
      ws['!cols'] = cols;
      return ws;
    };

    // == Sheet "TKB Giáo viên": all GV blocks stacked sequentially ==
    const buildGvSheet = () => {
      const gvsWithTKB = giaoViens.filter(gv => tkbs.some(t => String(t.gv_id) === String(gv.gv_id)));
      if (gvsWithTKB.length === 0) return null;

      const aoa = [];
      const merges = [];
      const borderSpecs = [];
      const nCols = 2 + thuList.length;

      gvsWithTKB.forEach((gv, gvIdx) => {
        // Title row
        aoa.push([`Giáo viên: ${gv.ten_gv}`]);
        // Header row
        aoa.push(['Buổi', 'Tiết', ...thuList.map(t => t.label)]);

        const dataStart = aoa.length;
        buoiList.forEach(buoi => {
          const buoiStart = aoa.length;
          tietList.forEach((tiet, idx) => {
            const row = [idx === 0 ? buoi.label : '', tiet];
            thuList.forEach(thu => {
              const tkb = tkbs.find(t => {
                if (String(t.gv_id) !== String(gv.gv_id)) return false;
                if (t.thu !== thu.value || t.buoi !== buoi.value) return false;
                const soTiet = Math.max(1, Number(t.so_tiet || 1));
                return tiet >= t.tiet && tiet <= (t.tiet + soTiet - 1);
              });
              row.push(tkb ? `${tkb.ten_lop || ''} - ${getMonShortName(tkb.ten_mon)}` : '');
            });
            aoa.push(row);
          });
          merges.push({ s: { r: buoiStart, c: 0 }, e: { r: buoiStart + tietList.length - 1, c: 0 } });
          // Bottom border after last tiết of this buổi
          borderSpecs.push({ r: buoiStart + tietList.length - 1, cStart: 0, cEnd: nCols - 1, side: 'bottom' });
        });
        const dataEnd = aoa.length - 1;
        // Right border on Tiết col and every Thu col for data rows
        for (let r = dataStart; r <= dataEnd; r++) {
          borderSpecs.push({ r, c: 1, side: 'right' });
          for (let thuIdx = 0; thuIdx < thuList.length; thuIdx++) {
            borderSpecs.push({ r, c: 2 + thuIdx, side: 'right' });
          }
        }
        if (gvIdx < gvsWithTKB.length - 1) aoa.push([]); // blank separator
      });

      const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
      ws['!merges'] = merges;
      applySpecs(ws, borderSpecs);
      const cols = [{ wch: 8 }, { wch: 6 }];
      thuList.forEach(() => cols.push({ wch: 22 }));
      ws['!cols'] = cols;
      return ws;
    };

    const lopSheet = buildLopSheet();
    if (lopSheet) { autoFitColumns(lopSheet); XLSXStyle.utils.book_append_sheet(workbook, lopSheet, 'TKB Lớp'); }
    const gvSheet = buildGvSheet();
    if (gvSheet) { autoFitColumns(gvSheet); XLSXStyle.utils.book_append_sheet(workbook, gvSheet, 'TKB Giáo viên'); }

    if (workbook.SheetNames.length === 0) {
      toast.warning('Không có dữ liệu TKB để xuất.');
      return;
    }

    const now = new Date();
    const fileName = `TKB_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;
    XLSXStyle.writeFile(workbook, fileName);
    toast.success(`Đã xuất file Excel "${fileName}" thành công!`);
  };

  const toggleHighlightTeacher = (gvId) => {
    if (!gvId) {
      setHighlightGvId(null);
      return;
    }
    setHighlightGvId((prev) => (String(prev) === String(gvId) ? prev : gvId));
  };

  const resultRowsByBuoi = useMemo(() => {
    const buildRows = (buoiValue) => {
      const built = [];
      for (const thu of thuList) {
        for (const tiet of tietList) {
          built.push({
            key: `${thu.value}-${buoiValue}-${tiet}`,
            thu: thu.value,
            buoi: buoiValue,
            tiet,
            thuLabel: thu.label
          });
        }
      }
      return built;
    };
    return {
      Sang: buildRows('Sang'),
      Chieu: buildRows('Chieu')
    };
  }, [thuList, tietList]);

  const activeLopsByBuoi = useMemo(() => {
    const lopIdsWithSang = new Set(tkbs.filter(t => t.buoi === 'Sang').map(t => t.lop_id));
    const lopIdsWithChieu = new Set(tkbs.filter(t => t.buoi === 'Chieu').map(t => t.lop_id));
    return {
      Sang: lopHocs.filter(lop => lopIdsWithSang.has(lop.lop_id)),
      Chieu: lopHocs.filter(lop => lopIdsWithChieu.has(lop.lop_id))
    };
  }, [tkbs, lopHocs]);

  const allLopMatrices = useMemo(() => {
    const result = {};
    tkbs.forEach(t => {
      if (!result[t.lop_id]) result[t.lop_id] = {};
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      for (let offset = 0; offset < soTiet; offset++) {
        result[t.lop_id][`${t.thu}_${t.buoi}_${Number(t.tiet) + offset}`] = t;
      }
    });
    return result;
  }, [tkbs]);

  const allGvMatrices = useMemo(() => {
    const result = {};
    tkbs.forEach(t => {
      if (!result[t.gv_id]) result[t.gv_id] = {};
      const soTiet = Math.max(1, Number(t.so_tiet || 1));
      for (let offset = 0; offset < soTiet; offset++) {
        result[t.gv_id][`${t.thu}_${t.buoi}_${Number(t.tiet) + offset}`] = t;
      }
    });
    return result;
  }, [tkbs]);

  const lopsWithTKB = useMemo(() => {
    const ids = new Set(tkbs.map(t => t.lop_id));
    return lopHocs.filter(l => ids.has(l.lop_id));
  }, [tkbs, lopHocs]);

  const gvsWithTKB = useMemo(() => {
    const ids = new Set(tkbs.map(t => String(t.gv_id)));
    return giaoViens.filter(g => ids.has(String(g.gv_id)));
  }, [tkbs, giaoViens]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="ket-qua-page">
      <div className="ket-qua-container">
        <div className="ket-qua-main-panel">
          {/* Toolbar */}
          <div className="result-toolbar">
            <button
              className={`btn btn-sm ${resultView === 'lop_sang' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setResultView('lop_sang')}
            >
              {t('ketQua.btnClassMorning')}
            </button>
            <button
              className={`btn btn-sm ${resultView === 'lop_chieu' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setResultView('lop_chieu')}
            >
              {t('ketQua.btnClassAfternoon')}
            </button>
            <button
              className={`btn btn-sm ${resultView === 'lop_view' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setResultView('lop_view')}
            >
              {t('ketQua.btnClassView')}
            </button>
            <button
              className={`btn btn-sm ${resultView === 'gv' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setResultView('gv')}
            >
              {t('ketQua.btnTeacherView')}
            </button>

            {/* Zoom controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
              <button
                className="btn btn-sm btn-success"
                onClick={handleExportExcel}
                disabled={tkbs.length === 0}
                title="Xuất toàn bộ TKB ra file Excel (Sáng, Chiều, từng GV)"
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <FiDownload />
                <span>{t('ketQua.exportExcel')}</span>
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="ket-qua-content">

            {/* TKB các lớp buổi sáng / chiều */}
            {(resultView === 'lop_sang' || resultView === 'lop_chieu') && (() => {
              const buoiKey = resultView === 'lop_sang' ? 'Sang' : 'Chieu';
              const activeLops = activeLopsByBuoi[buoiKey];
              const buoiRows = resultRowsByBuoi[buoiKey];

              if (lopHocs.length === 0) {
                return (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <p>Chưa có lớp học nào. Vui lòng thêm lớp học trước.</p>
                  </div>
                );
              }
              if (activeLops.length === 0) {
                return (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <p>Chưa có TKB buổi {buoiKey === 'Sang' ? 'sáng' : 'chiều'} nào.</p>
                  </div>
                );
              }

              return (
                <div className="tkb-table-wrapper" style={{ padding: '10px' }}>
                  <table className={`tkb-table result-tkb-table ${highlightGvId ? 'has-highlight' : ''}`}>
                    <thead>
                      <tr>
                        <th className="sticky-col header-cell">Thứ</th>
                        <th className="sticky-col-3 header-cell">Tiết</th>
                        {activeLops.map(lop => (
                          <React.Fragment key={lop.lop_id}>
                            <th className="lop-header" colSpan={2}>{lop.ten_lop}</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {buoiRows.map((row) => {
                        const showThu = row.tiet === 1;
                        return (
                          <tr key={row.key} className={showThu ? 'thu-separator' : ''}>
                            {showThu && (
                              <td className="sticky-col thu-cell" rowSpan={5}>{row.thuLabel}</td>
                            )}
                            <td className="sticky-col-3 tiet-cell">{row.tiet}</td>
                            {activeLops.map(lop => {
                              const tkb = findTKB(lop.lop_id, row.thu, row.buoi, row.tiet);
                              const isHighlighted = tkb && highlightGvId && String(tkb.gv_id) === String(highlightGvId);
                              const bg = tkb ? getGvColor(tkb.gv_id) : undefined;
                              const fg = bg ? getContrastTextColor(bg) : undefined;
                              return (
                                <React.Fragment key={`${row.key}-${lop.lop_id}`}>
                                  <td
                                    className={`tkb-cell mon-col ${tkb ? 'has-tkb has-gv' : ''} ${isHighlighted ? 'gv-highlight' : ''}`}
                                    style={bg ? { backgroundColor: bg, color: fg } : undefined}
                                    title={tkb ? `${tkb.ten_mon} - ${tkb.ten_gv}` : ''}
                                    onClick={() => { if (tkb) toggleHighlightTeacher(tkb.gv_id); else setHighlightGvId(null); }}
                                  >
                                    {tkb ? (
                                      <div className="tkb-content">
                                        <span className="mon-name">{getMonShortName(tkb.ten_mon)}</span>
                                      </div>
                                    ) : null}
                                  </td>
                                  <td
                                    className={`tkb-cell gv-col ${tkb ? 'has-tkb has-gv' : ''} ${isHighlighted ? 'gv-highlight' : ''}`}
                                    style={bg ? { backgroundColor: bg, color: fg, opacity: 0.85 } : undefined}
                                    title={tkb ? `${tkb.ten_gv}` : ''}
                                    onClick={() => { if (tkb) toggleHighlightTeacher(tkb.gv_id); else setHighlightGvId(null); }}
                                  >
                                    {tkb ? (
                                      <div className="tkb-content">
                                        <span className="gv-short-name">{getGvShortName(tkb.ten_gv)}</span>
                                      </div>
                                    ) : null}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* TKB từng lớp - hiển thị tất cả lần lượt */}
            {resultView === 'lop_view' && (
              <div className="tkb-seq-wrapper">
                {lopsWithTKB.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <p>Chưa có TKB nào được xếp.</p>
                  </div>
                ) : (
                  <div className="sequential-grid">
                  {lopsWithTKB.map(lop => {
                    const matrix = allLopMatrices[lop.lop_id] || {};
                    return (
                      <div key={lop.lop_id} className="sequential-block">
                        <div className="view-title-banner">
                          TKB LỚP:&nbsp;<strong>{lop.ten_lop}</strong>
                          {lop.gvcn && <span className="banner-sub"> · GVCN: {lop.gvcn}</span>}
                          {lop.phong && <span className="banner-sub"> · Phòng: {lop.phong}</span>}
                        </div>
                        <table className="tkb-table result-lop-view-table">
                          <thead>
                            <tr>
                              <th className="header-cell buoi-th">Buổi</th>
                              <th className="header-cell tiet-th">Tiết</th>
                              {thuList.map(thu => (
                                <th key={thu.value} className="header-cell thu-th">{thu.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {buoiList.flatMap((buoi) =>
                              tietList.map((tiet, tietIndex) => (
                                <tr key={`${buoi.value}-${tiet}`} className={tiet === tietList[tietList.length - 1] ? 'buoi-end-row' : ''}>
                                  {tietIndex === 0 && (
                                    <td rowSpan={tietList.length} className={`buoi-cell buoi-${buoi.value.toLowerCase()}`}>
                                      {buoi.label}
                                    </td>
                                  )}
                                  <td className="tiet-cell">{tiet}</td>
                                  {thuList.map(thu => {
                                    const tkb = matrix[`${thu.value}_${buoi.value}_${tiet}`];
                                    const bg = tkb ? getGvColor(tkb.gv_id) : undefined;
                                    const fg = bg ? getContrastTextColor(bg) : undefined;
                                    return (
                                      <td
                                        key={thu.value}
                                        className={`tkb-cell thu-col ${tkb ? 'has-tkb' : ''}`}
                                        style={bg ? { backgroundColor: bg, color: fg } : undefined}
                                        title={tkb ? `${tkb.ten_mon} - ${tkb.ten_gv}` : ''}
                                      >
                                        {tkb && (
                                          <div className="tkb-cell-inner">
                                            <span className="cell-mon">{tkb.ten_mon}</span>
                                            <span className="cell-gv">{getGvShortName(tkb.ten_gv)}</span>
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>
            )}

            {/* TKB từng GV - hiển thị tất cả lần lượt */}
            {resultView === 'gv' && (
              <div className="tkb-seq-wrapper">
                {gvsWithTKB.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                    <p>Chưa có TKB nào được xếp.</p>
                  </div>
                ) : (
                  <div className="sequential-grid">
                  {gvsWithTKB.map(gv => {
                    const matrix = allGvMatrices[gv.gv_id] || {};
                    return (
                      <div key={gv.gv_id} className="sequential-block">
                        <div className="view-title-banner gv-title-banner">
                          GIÁO VIÊN:&nbsp;<strong>{gv.ten_gv}</strong>
                          {gv.to_bo_mon && <span className="banner-sub"> · {gv.to_bo_mon}</span>}
                        </div>
                        <table className="tkb-table result-gv-table">
                          <thead>
                            <tr>
                              <th className="header-cell buoi-th">Buổi</th>
                              <th className="header-cell tiet-th">Tiết</th>
                              {thuList.map(thu => (
                                <th key={thu.value} className="header-cell thu-th">{thu.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {buoiList.flatMap((buoi) =>
                              tietList.map((tiet, tietIndex) => (
                                <tr key={`${buoi.value}-${tiet}`} className={tiet === tietList[tietList.length - 1] ? 'buoi-end-row' : ''}>
                                  {tietIndex === 0 && (
                                    <td rowSpan={tietList.length} className={`buoi-cell buoi-${buoi.value.toLowerCase()}`}>
                                      {buoi.label}
                                    </td>
                                  )}
                                  <td className="tiet-cell">{tiet}</td>
                                  {thuList.map(thu => {
                                    const tkb = matrix[`${thu.value}_${buoi.value}_${tiet}`];
                                    const bg = tkb ? getLopColor(tkb.lop_id) : undefined;
                                    const fg = bg ? getContrastTextColor(bg) : undefined;
                                    return (
                                      <td
                                        key={thu.value}
                                        className={`tkb-cell thu-col ${tkb ? 'has-tkb' : ''}`}
                                        style={bg ? { backgroundColor: bg, color: fg } : undefined}
                                        title={tkb ? `${tkb.ten_lop} - ${tkb.ten_mon}` : ''}
                                      >
                                        {tkb && (
                                          <div className="tkb-cell-inner">
                                            <span className="cell-lop">{tkb.ten_lop}</span>
                                            <span className="cell-mon-sm">{getMonShortName(tkb.ten_mon)}</span>
                                          </div>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .ket-qua-page {
          height: 100%;
        }

        .ket-qua-container {
          height: calc(100vh - 112px);
          display: flex;
          flex-direction: column;
        }

        .ket-qua-main-panel {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex: 1;
        }

        .result-toolbar {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          padding: 12px 20px;
          border-bottom: 1px solid #eee;
        }

        .ket-qua-content {
          flex: 1;
          overflow: auto;
        }

        .tkb-table-wrapper {
          flex: 1;
          overflow: auto;
          padding: 0;
          user-select: none;
        }

        /* Wrapper cho lop_view / gv view (flex-wrap grid) */
        .tkb-seq-wrapper {
          flex: 1;
          overflow: auto;
          user-select: none;
        }

        .tkb-table {
          border-collapse: collapse;
          font-size: 12px;
          width: max-content;
          min-width: 100%;
        }

        /* Bảng trong sequential block không cần min-width 100% */
        .sequential-block .tkb-table {
          min-width: unset;
        }

        .tkb-table th,
        .tkb-table td {
          border: 1px solid #ddd;
          padding: 4px;
          text-align: center;
          min-width: 60px;
          height: 32px;
        }

        .tkb-table th {
          background: #f5f5f5;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 3;
        }

        .header-cell {
          background: #e0e0e0 !important;
        }

        .sticky-col {
          position: sticky;
          left: 0;
          background: #f0f0f0;
          z-index: 4;
          min-width: 35px;
          font-weight: bold;
        }

        .sticky-col-3 {
          position: sticky;
          left: 35px;
          background: #fafafa;
          z-index: 4;
          min-width: 30px;
        }

        .thu-cell {
          background: #e3f2fd !important;
          font-weight: bold;
          font-size: 14px;
          border-right: 2px solid #9e9e9e !important;
        }

        .tiet-cell {
          background: #f5f5f5 !important;
          border-right: 2px solid #9e9e9e !important;
        }

        /* Only separate days horizontally */
        .thu-separator td,
        .thu-separator th {
          border-top: 2px solid #2e7d32 !important;
        }

        /* Remove all individual cell borders in result-tkb-table, redraw only needed ones */
        .result-tkb-table th,
        .result-tkb-table td {
          border: none;
        }

        /* Restore day separator */
        .result-tkb-table .thu-separator td,
        .result-tkb-table .thu-separator th {
          border-top: 2px solid #2e7d32 !important;
        }

        /* Vertical divider between lớp groups (right edge of each gv-col) */
        .result-tkb-table .gv-col {
          border-right: 2px solid #bdbdbd !important;
        }

        /* Vertical divider after sticky cols */
        .result-tkb-table .sticky-col {
          border-right: 2px solid #9e9e9e !important;
        }

        .result-tkb-table .sticky-col-3 {
          border-right: 2px solid #9e9e9e !important;
        }

        /* Right border on lop-header (colSpan=2) aligns with gv-col right border */
        .result-tkb-table .lop-header {
          border-right: 2px solid #bdbdbd !important;
        }

        .lop-header {
          background: #e8f5e9 !important;
          min-width: 70px;
        }

        .tkb-cell {
          cursor: pointer;
          transition: all 0.15s;
          vertical-align: middle;
        }

        .tkb-cell:not(.has-gv):hover {
          background: #e3f2fd;
        }

        .tkb-cell.has-tkb:not(.has-gv) {
          background: #fff9c4;
        }

        .tkb-cell.has-tkb:not(.has-gv):hover {
          background: #fff59d;
        }

        .tkb-cell.has-gv:hover {
          filter: brightness(0.95);
        }

        .tkb-cell.gv-highlight {
          filter: brightness(1.25) saturate(1.15);
          box-shadow:
            inset 0 0 0 4px rgba(0, 0, 0, 0.55),
            0 0 0 2px rgba(255, 255, 255, 0.65),
            0 6px 16px rgba(0, 0, 0, 0.25);
        }

        .tkb-table.has-highlight .tkb-cell.has-gv:not(.gv-highlight) {
          opacity: 0.35;
          filter: grayscale(0.35);
        }

        .tkb-table.has-highlight .tkb-cell.has-gv:not(.gv-highlight):hover {
          opacity: 0.55;
        }

        .tkb-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }

        .mon-name {
          font-weight: 600;
          font-size: 11px;
          color: inherit;
        }

        .result-tkb-table .mon-col {
          text-align: left;
          padding-left: 5px;
          min-width: 55px;
        }

        .result-tkb-table .gv-col {
          text-align: right;
          padding-right: 5px;
          min-width: 44px !important;
          max-width: 60px;
        }

        .gv-short-name {
          font-size: 10px;
          font-style: italic;
          color: inherit;
          white-space: nowrap;
        }

        /* ===== Sequential grid layout ===== */
        .sequential-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          padding: 12px;
          align-items: flex-start;
        }

        /* ===== Sequential blocks ===== */
        .sequential-block {
          flex: 0 0 auto;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 5px rgba(0,0,0,0.13);
        }
        .banner-sub {
          font-weight: 400;
          font-size: 11px;
          opacity: 0.82;
          margin-left: 4px;
        }

        /* ===== View title banner ===== */
        .view-title-banner {
          background: linear-gradient(135deg, #1565c0 0%, #1976d2 100%);
          color: white;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }
        .gv-title-banner {
          background: linear-gradient(135deg, #2e7d32 0%, #388e3c 100%);
        }

        /* ===== Shared header styles for lop_view / gv tables ===== */
        .result-lop-view-table th,
        .result-gv-table th {
          background: #37474f !important;
          color: #fff !important;
          font-weight: 600;
          font-size: 11px;
          padding: 5px 4px;
          border: none;
          white-space: nowrap;
        }
        .result-lop-view-table .buoi-th,
        .result-gv-table .buoi-th {
          background: #263238 !important;
          min-width: 40px !important;
          border-right: 2px solid #546e7a !important;
        }
        .result-lop-view-table .tiet-th,
        .result-gv-table .tiet-th {
          background: #263238 !important;
          min-width: 28px !important;
          border-right: 2px solid #546e7a !important;
        }
        .result-lop-view-table .thu-th,
        .result-gv-table .thu-th {
          min-width: 80px !important;
          border-right: 1px solid #546e7a !important;
        }

        /* ===== Buổi cells ===== */
        .buoi-cell {
          font-weight: 700;
          font-size: 11px;
          text-align: center;
          vertical-align: middle;
          letter-spacing: 0.2px;
          white-space: nowrap;
          width: 40px;
        }
        .buoi-sang {
          background: linear-gradient(180deg, #fff8e1 0%, #ffe082 100%) !important;
          color: #e65100 !important;
          border-right: 3px solid #ef6c00 !important;
        }
        .buoi-chieu {
          background: linear-gradient(180deg, #e3f2fd 0%, #90caf9 100%) !important;
          color: #0d47a1 !important;
          border-right: 3px solid #1565c0 !important;
        }

        /* ===== Data rows ===== */
        .result-lop-view-table td,
        .result-gv-table td {
          border: none;
          height: 36px;
          padding: 2px 4px;
          vertical-align: middle;
        }
        .result-lop-view-table .tiet-cell,
        .result-gv-table .tiet-cell {
          background: #eceff1 !important;
          color: #37474f;
          font-weight: 700;
          font-size: 12px;
          text-align: center;
          border-right: 2px solid #78909c !important;
          min-width: 28px;
        }
        .result-lop-view-table .thu-col,
        .result-gv-table .thu-col {
          border-right: 1px solid #e0e0e0 !important;
          min-width: 80px;
        }
        .result-lop-view-table tr.buoi-end-row td,
        .result-gv-table tr.buoi-end-row td {
          border-bottom: 2px solid #78909c !important;
        }

        /* ===== Two-line cell content ===== */
        .tkb-cell-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          line-height: 1.25;
        }
        .cell-mon {
          font-weight: 700;
          font-size: 12px;
          color: inherit;
          text-align: center;
        }
        .cell-gv {
          font-size: 10px;
          font-style: italic;
          color: inherit;
          opacity: 0.82;
          text-align: center;
          white-space: nowrap;
        }
        .cell-lop {
          font-weight: 800;
          font-size: 13px;
          color: inherit;
          text-align: center;
        }
        .cell-mon-sm {
          font-size: 10px;
          font-style: italic;
          color: inherit;
          opacity: 0.82;
          text-align: center;
        }

        /* ===== Empty prompt ===== */
        .empty-prompt {
          padding: 36px !important;
          text-align: center !important;
          color: #9e9e9e;
          font-style: italic;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};

export default KetQuaTKBPage;
